/**
 * @packageDocumentation
 * The Webcomponent SDK is tailored for using Myop components packaged as separate bundles and exposed as web components.<br/>
 * Developers importing this SDK can leverage APIs and utilities specifically crafted to support the lifecycle management and communication patterns required for web components within the Myop architecture.<br/>
 * This SDK promotes reusability and encapsulation, enabling developers to build modular, self-contained components that can be seamlessly integrated into the host application's DOM structure.
 *
 *
 * Once loaded the `RootSDK`, you can load the `IframeSDK`
 * ```js
 * const {WebComponentSDK} = (await window.myop.rootSDK.getWebcomponentModule());
 * ```
 * or
 * ```ts
 * import {getWebcomponentModule} from '@myop/sdk';
 * const {WebComponentSDK} = (await getWebcomponentModule());
 * ```
 * or
 * ```ts
 * import {WebComponentSDK} from '@myop/sdk/webcomponent';
 * ```
 *
 */
import {BaseMyopSDK} from "../common";
import {MyopMessages} from "../common/MyopMessages";
import {myopWebcomponentConsts} from "./consts";
import {BaseWebComponentMessageHandler} from "./messageHandlers/BaseWebComponentMessageHandler";
import {BindOnClickHandler} from "./messageHandlers/bindOnClickHandler";
import {ChangeTextHandler} from "./messageHandlers/changeTextHandler";
import {ShadowRootRef} from "./shadowRootRef";
import {DetectMyopRefsMessageHandler} from "./messageHandlers/DetectMyopRefsMessageHandler";
import {defineDynamicImport} from "../dynamicImport/defineDynamicImport";
import {
    BaseMyopMessage,
    InitMessage,
    InitRequest,
    MessageDirection,
    MyopBindMessage,
    MyopBindReplayMessage
} from "../messages";
import {setAttributeHandler} from "./messageHandlers/setAttributeHandler.ts";
import {AddEventListenerHandler} from "./messageHandlers/addEventListenerHandler.ts";
import {CreateRefComponentMessageHandler} from "./messageHandlers/CreateRefComponentMessageHandler.ts";
import {ExecuteScriptHandler} from "./messageHandlers/executeScriptHandler.ts";
import {GetElementValueHandler} from "./messageHandlers/GetElementValueHandler.ts";
import {SetInnerHtmlHandler} from "./messageHandlers/setInnerHtmlHandler.ts";

/**
 * The Webcomponent SDK is tailored for using Myop components packaged as separate bundles and exposed as web components.<br/>
 * Developers importing this SDK can leverage APIs and utilities specifically crafted to support the lifecycle management and communication patterns required for web components within the Myop architecture.<br/>
 * This SDK promotes reusability and encapsulation, enabling developers to build modular, self-contained components that can be seamlessly integrated into the host application's DOM structure.
 *
 *
 * Once loaded the `Root SDK`, you can load the `IframeSDK`
 * ```js
 * const {WebComponentSDK} = (await window.myop.rootSDK.getWebcomponentModule());
 * ```
 * or
 * ```ts
 * import {getWebcomponentModule} from '@myop/sdk';
 * const {WebComponentSDK} = (await getWebcomponentModule());
 * ```
 *
 */
export class WebComponentSDK extends BaseMyopSDK {

    alwaysPassEnvelopesToHost = true;

    //when new web component created.
    connectedCallback = (container: HTMLElement, shadow: ShadowRoot) => {
        container.addEventListener(myopWebcomponentConsts.webcomponent_message_key, (e: any) => {
            //debugger;
            const eventContent = e.data ? e.data : e.detail;
            if (!eventContent || !eventContent.myop) {
                debugger;
                throw new Error('recvied webcomponent_message with wrong type')
            } else {
                const message = eventContent as BaseMyopMessage;

                //TODO : send container / compoennt identifier for multil components support
                this.handleIncomeMessages(e)

                if (message.messageType === MyopMessages.InitMessage) {
                    // @ts-ignore
                    const newComponentId = (message as InitMessage).content.id;
                    this.components[newComponentId].context = new ShadowRootRef(container, shadow)

                    // this.supportedHandlers.forEach(handler => {
                    //     handler.context = this.shadowRoots;
                    // });
                }
            }
        })

        //TODO : fix that hack. race condition with host.load when module loaded
        setTimeout(() => {
            this.messageToHost(new InitRequest())
        }, 1)
    }

    messageToHost = (message: MyopBindReplayMessage, replayTo?: MyopBindMessage) => {
        message.direction = MessageDirection.up
        //message.replayToHandler = replayTo?.handlerUniqueId;

        if (!message.replayToHandler && replayTo?.handlerUniqueId)
            message.replayToHandler = replayTo.handlerUniqueId

        window.myop.hostSDK.send(message)
    }

    supportedHandlers: BaseWebComponentMessageHandler[] = [
        new ChangeTextHandler(this.messageToHost, this.components),
        new SetInnerHtmlHandler(this.messageToHost, this.components),
        new BindOnClickHandler(this.messageToHost, this.components),
        new DetectMyopRefsMessageHandler(this.messageToHost, this.components),
        new setAttributeHandler(this.messageToHost, this.components),
        new AddEventListenerHandler(this.messageToHost, this.components),
        new ExecuteScriptHandler(this.messageToHost, this.components),
        new GetElementValueHandler(this.messageToHost, this.components),
        //
        new CreateRefComponentMessageHandler(this.messageToHost, this.components)
    ];

    public init = () => {
        //this.myComponentId
        //this.handleIncomeMessages
    }
}

defineDynamicImport('WebComponentSDK', WebComponentSDK);
